import { useEffect, useRef, useState } from 'react'

import { giftBonus } from '@/assets/icons'
import { useTranslate } from '@/i18n/useTranslate'
import { formatPromotionTotalAmount } from '@/utilities/depositBonusHelper'
import { normalizePromoCode } from '@/utilities/referralHelper'

import DepositRadialRing from './depositRadialRing'

export type DepositClaimStatus = 'none' | 'pending' | 'success' | 'error'
export type DepositFlowStatus = 'processing' | 'promo' | 'failed' | 'success'

type DepositSuccessAnimationTemplateProps = {
    amount: number
    currency?: string
    vendorName?: string
    vendorLogoUrl?: string | null
    accountId?: string
    failedAttempts?: number
    maxAttempts?: number
    status?: DepositFlowStatus
    isSubmitting?: boolean
    onRetry?: () => void
    onChangeAccountId?: () => void
    onShowVoucher?: () => void
    promoCode?: string
    onPromoCodeChange?: (code: string) => void
    onComplete: () => void
    /** Fired once after the base amount has finished counting. */
    onBaseReady?: () => void
    claimStatus?: DepositClaimStatus
    claimRewardAmount?: number
    /** Known bonus amount shown while the reward voucher is being issued. */
    pendingRewardAmount?: number
    pendingRewardCurrency?: string
    durationMs?: number
    totalDurationMs?: number
    holdMs?: number
}

type Phase =
    | 'enter'
    | 'counting'
    | 'awaiting'
    | 'base-done'
    | 'bonus-badge'
    | 'totaling'
    | 'done'

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

const PROMO_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'.split('')

type AttemptNodeState = 'failed' | 'active' | 'success' | 'upcoming'

const getAttemptNodeState = (
    index: number,
    failedCount: number,
    status: DepositFlowStatus,
    isComplete = false
): AttemptNodeState => {
    if (index < failedCount) return 'failed'
    if (status === 'failed') return 'upcoming'
    if (index === failedCount) {
        return status === 'success' && isComplete ? 'success' : 'active'
    }
    return 'upcoming'
}

const StepFailIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
            d="M7 7l10 10M17 7L7 17"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
        />
    </svg>
)

const StepSuccessIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
            d="M6 12.5l4 4 8-8.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.6"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const EMPTY_HOLD_MS = 120

export default function DepositSuccessAnimationTemplate({
    amount,
    currency = 'RSD',
    vendorName = '',
    vendorLogoUrl,
    accountId = '',
    failedAttempts = 0,
    maxAttempts = 3,
    status = 'success',
    isSubmitting = false,
    onRetry,
    onChangeAccountId,
    onShowVoucher,
    promoCode = '',
    onPromoCodeChange,
    onComplete,
    onBaseReady,
    claimStatus = 'none',
    claimRewardAmount = 0,
    pendingRewardAmount = 0,
    pendingRewardCurrency,
    durationMs = 3300,
    totalDurationMs = 2000,
    holdMs = 1400
}: DepositSuccessAnimationTemplateProps) {
    const { t } = useTranslate()
    const [displayAmount, setDisplayAmount] = useState(0)
    const [settledAmount, setSettledAmount] = useState<number | null>(null)
    const [ringProgress, setRingProgress] = useState(0)
    const [goldProgress, setGoldProgress] = useState(0)
    const [phase, setPhase] = useState<Phase>('enter')
    const onCompleteRef = useRef(onComplete)
    const onBaseReadyRef = useRef(onBaseReady)
    const baseReadyFiredRef = useRef(false)
    const displayAmountRef = useRef(0)
    const ringProgressRef = useRef(0)
    const settledAmountRef = useRef<number | null>(null)
    const statusRef = useRef(status)
    const previousStatusRef = useRef<DepositFlowStatus | null>(null)
    const targetAmount = Math.max(0, amount)
    const targetReward = Math.max(0, claimRewardAmount)
    const anticipatedReward = Math.max(0, pendingRewardAmount)
    const anticipatedRewardCurrency = pendingRewardCurrency || currency
    const totalAmount = targetAmount + targetReward
    const hasReward = claimStatus === 'success' && targetReward > 0
    const isSuccess = status === 'success'
    const isFailed = status === 'failed'
    const isPromo = status === 'promo'
    const isProcessing = status === 'processing'
    const isAwaitingResult = isProcessing && phase === 'awaiting'
    const isAwaitingBonus =
        isSuccess && phase === 'base-done' && claimStatus === 'pending'
    const showGold = isSuccess && hasReward && goldProgress > 0.02
    const showBadge = isSuccess && hasReward && phase === 'bonus-badge'
    const clampedFailedAttempts = Math.min(Math.max(failedAttempts, 0), maxAttempts)
    const clampedAttempt = Math.min(clampedFailedAttempts + 1, maxAttempts)
    const canRetry = clampedFailedAttempts < maxAttempts
    const isFinalFailure = isFailed && clampedFailedAttempts >= maxAttempts
    const showVoucherOption = isFailed && clampedFailedAttempts >= 1 && Boolean(onShowVoucher)
    const showMeta = Boolean(vendorName || accountId || targetAmount)
    const displayAttempt = isFailed
        ? Math.min(Math.max(clampedFailedAttempts, 1), maxAttempts)
        : clampedAttempt
    const isLastAttempt = displayAttempt >= maxAttempts
    const ringJustCompleted =
        isSuccess &&
        (phase === 'base-done' ||
            phase === 'bonus-badge' ||
            phase === 'totaling' ||
            phase === 'done')
    const headingTitle = isFailed
        ? t('directBettingConfirmation.errorTitle')
        : isPromo
          ? t('directBettingConfirmation.promoTitle')
          : ringJustCompleted
            ? t('directBettingConfirmation.titleSuccess')
            : t('directBettingConfirmation.loadingTitle')
    const formattedStaticAmount = formatPromotionTotalAmount(targetAmount)
    const visibleAmount = isFailed
        ? Math.max(targetAmount, settledAmount ?? 0)
        : showGold || phase === 'totaling' || phase === 'done'
          ? displayAmount
          : settledAmount != null
            ? Math.max(displayAmount, settledAmount, targetAmount)
            : displayAmount
    const formattedAmount = formatPromotionTotalAmount(visibleAmount)
    const formattedAnticipatedReward =
        formatPromotionTotalAmount(anticipatedReward)

    displayAmountRef.current = displayAmount
    ringProgressRef.current = ringProgress
    statusRef.current = status
    const formattedReward = formatPromotionTotalAmount(targetReward)

    useEffect(() => {
        onCompleteRef.current = onComplete
    }, [onComplete])

    useEffect(() => {
        onBaseReadyRef.current = onBaseReady
    }, [onBaseReady])

    useEffect(() => {
        if (!isProcessing && !isSuccess) {
            window.dispatchEvent(new Event('inactivity-resume'))
            return
        }

        window.dispatchEvent(new Event('inactivity-pause'))
        return () => {
            window.dispatchEvent(new Event('inactivity-resume'))
        }
    }, [isProcessing, isSuccess])

    useEffect(() => {
        if (isFailed) {
            previousStatusRef.current = status
            setGoldProgress(0)
            setRingProgress((current) => {
                const next = current > 0.12 ? current : 1
                ringProgressRef.current = next
                return next
            })
            return
        }

        if (isPromo) {
            previousStatusRef.current = status
            settledAmountRef.current = null
            displayAmountRef.current = 0
            ringProgressRef.current = 0
            setSettledAmount(null)
            setPhase('enter')
            setDisplayAmount(0)
            setRingProgress(0)
            setGoldProgress(0)
            baseReadyFiredRef.current = false
            return
        }

        if (isProcessing) {
            const previousStatus = previousStatusRef.current
            const isNewAttempt =
                previousStatus != null && previousStatus !== 'processing'
            previousStatusRef.current = status

            if (!isNewAttempt && settledAmountRef.current != null) {
                return
            }

            if (!isNewAttempt && previousStatus === 'processing') {
                return
            }

            if (isNewAttempt) {
                settledAmountRef.current = null
                setSettledAmount(null)
            }

            displayAmountRef.current = 0
            ringProgressRef.current = 0
            setPhase('enter')
            setDisplayAmount(0)
            setRingProgress(0)
            setGoldProgress(0)
            baseReadyFiredRef.current = false
            const enterTimer = window.setTimeout(
                () => setPhase('counting'),
                EMPTY_HOLD_MS
            )
            return () => window.clearTimeout(enterTimer)
        }

        previousStatusRef.current = status

        if (isSuccess) {
            setPhase((current) => {
                if (current === 'enter') return 'counting'
                if (current === 'awaiting') return 'base-done'
                return current
            })
        }
    }, [isFailed, isProcessing, isPromo, isSuccess, status])

    useEffect(() => {
        if (settledAmount != null && targetAmount > settledAmount) {
            settledAmountRef.current = targetAmount
            displayAmountRef.current = targetAmount
            setSettledAmount(targetAmount)
            setDisplayAmount(targetAmount)
        }
    }, [settledAmount, targetAmount])

    useEffect(() => {
        if (phase !== 'counting') return
        if (isFailed || isPromo) return
        if (targetAmount <= 0) return

        if (
            settledAmountRef.current != null &&
            settledAmountRef.current >= targetAmount
        ) {
            return
        }

        let frameId = 0
        const startedAt = performance.now()
        const fromProgress = ringProgressRef.current
        const fromAmount = displayAmountRef.current
        const progressDelta = Math.max(0, 1 - fromProgress)
        const amountDelta = targetAmount - fromAmount

        const finishFill = () => {
            const latched = targetAmount
            settledAmountRef.current = latched
            displayAmountRef.current = latched
            ringProgressRef.current = 1
            setSettledAmount(latched)
            setDisplayAmount(latched)
            setRingProgress(1)
            setPhase(
                statusRef.current === 'success' ? 'base-done' : 'awaiting'
            )
        }

        if (progressDelta < 0.002) {
            finishFill()
            return
        }

        const duration = Math.max(280, durationMs * progressDelta)

        const tick = (now: number) => {
            const progress = Math.min((now - startedAt) / duration, 1)
            const eased = easeOutCubic(progress)
            const nextAmount = fromAmount + amountDelta * eased
            const nextProgress = fromProgress + progressDelta * eased
            displayAmountRef.current = nextAmount
            ringProgressRef.current = nextProgress
            setDisplayAmount(nextAmount)
            setRingProgress(nextProgress)

            if (progress < 1) {
                frameId = window.requestAnimationFrame(tick)
                return
            }

            finishFill()
        }

        frameId = window.requestAnimationFrame(tick)
        return () => {
            window.cancelAnimationFrame(frameId)
            if (
                targetAmount > 0 &&
                ringProgressRef.current >= 0.9 &&
                settledAmountRef.current == null
            ) {
                finishFill()
            }
        }
    }, [durationMs, isFailed, isPromo, phase, targetAmount])

    useEffect(() => {
        if (!isSuccess || phase !== 'base-done') return
        if (baseReadyFiredRef.current) return
        baseReadyFiredRef.current = true
        onBaseReadyRef.current?.()
    }, [isSuccess, phase])

    useEffect(() => {
        if (!isSuccess || phase !== 'base-done') return

        if (claimStatus === 'pending') {
            return
        }

        if (hasReward) {
            const revealTimer = window.setTimeout(
                () => setPhase('bonus-badge'),
                280
            )
            return () => window.clearTimeout(revealTimer)
        }

        const holdTimer = window.setTimeout(() => setPhase('done'), holdMs)
        return () => window.clearTimeout(holdTimer)
    }, [claimStatus, hasReward, holdMs, isSuccess, phase])

    useEffect(() => {
        if (!isSuccess || phase !== 'bonus-badge') return

        const holdTimer = window.setTimeout(() => setPhase('totaling'), 360)
        return () => window.clearTimeout(holdTimer)
    }, [isSuccess, phase])

    useEffect(() => {
        if (!isSuccess || phase !== 'totaling') return

        let frameId = 0
        const startedAt = performance.now()
        const fromAmount = targetAmount
        const toAmount = hasReward ? totalAmount : targetAmount
        const amountDelta = toAmount - fromAmount

        const tick = (now: number) => {
            const progress = Math.min((now - startedAt) / totalDurationMs, 1)
            const eased = easeOutCubic(progress)
            setDisplayAmount(fromAmount + amountDelta * eased)
            setGoldProgress(hasReward ? eased : 0)

            if (progress < 1) {
                frameId = window.requestAnimationFrame(tick)
                return
            }

            setDisplayAmount(toAmount)
            setGoldProgress(hasReward ? 1 : 0)
            setPhase('done')
        }

        frameId = window.requestAnimationFrame(tick)
        return () => window.cancelAnimationFrame(frameId)
    }, [hasReward, isSuccess, phase, targetAmount, totalAmount, totalDurationMs])

    useEffect(() => {
        if (!isSuccess || phase !== 'done') return

        const holdTimer = window.setTimeout(() => {
            onCompleteRef.current()
        }, holdMs)

        return () => window.clearTimeout(holdTimer)
    }, [holdMs, isSuccess, phase])

    return (
        <div
            className={
                showGold
                    ? 'deposit-success-animation deposit-success-animation--success deposit-success-animation--credited'
                    : status === 'failed'
                      ? 'deposit-success-animation deposit-success-animation--failed'
                      : status === 'promo'
                        ? 'deposit-success-animation deposit-success-animation--promo'
                        : status === 'processing'
                          ? 'deposit-success-animation deposit-success-animation--processing'
                          : 'deposit-success-animation deposit-success-animation--success'
            }>
            {showMeta ? (
                <div className="deposit-success-animation__meta">
                    <div className="deposit-success-animation__heading">
                        <p>{t('directBettingConfirmation.title')}</p>
                        <h1>{headingTitle}</h1>
                    </div>

                    <div className="deposit-success-animation__cards">
                        <article className="deposit-success-animation__card">
                            {vendorLogoUrl || vendorName ? (
                                <div className="deposit-success-animation__card-logo">
                                    {vendorLogoUrl ? (
                                        <img
                                            src={vendorLogoUrl}
                                            alt={vendorName}
                                            width={72}
                                            height={72}
                                        />
                                    ) : (
                                        <span aria-hidden="true">
                                            {vendorName
                                                .slice(0, 2)
                                                .toUpperCase()}
                                        </span>
                                    )}
                                </div>
                            ) : null}
                            <div className="deposit-success-animation__card-copy">
                                <p>
                                    {t(
                                        'depositSuccessAnimation.accountIdLabel'
                                    )}
                                </p>
                                <strong>{accountId || vendorName}</strong>
                            </div>
                        </article>

                        <article className="deposit-success-animation__card">
                            <div className="deposit-success-animation__card-copy">
                                <p>
                                    {t(
                                        'depositSuccessAnimation.insertedAmountLabel'
                                    )}
                                </p>
                                <strong>
                                    {formattedStaticAmount} {currency}
                                </strong>
                            </div>
                        </article>
                    </div>

                    <div className="deposit-success-animation__attempts">
                        <div className="deposit-success-animation__attempts-head">
                            <p>{t('directBettingConfirmation.statusLabel')}</p>
                            <p
                                className={
                                    isLastAttempt
                                        ? 'deposit-success-animation__attempts-count deposit-success-animation__attempts-count--last'
                                        : 'deposit-success-animation__attempts-count'
                                }>
                                {t('directBettingConfirmation.attemptLabel', {
                                    attempt: displayAttempt,
                                    total: maxAttempts
                                })}
                                {isLastAttempt
                                    ? ` · ${t('depositSuccessAnimation.lastAttemptHint')}`
                                    : ''}
                            </p>
                        </div>

                        <ol className="deposit-success-animation__stepper">
                            {Array.from({ length: maxAttempts }).map(
                                (_, index) => {
                                    const nodeState = getAttemptNodeState(
                                        index,
                                        clampedFailedAttempts,
                                        status,
                                        ringJustCompleted
                                    )
                                    const previousState =
                                        index > 0
                                            ? getAttemptNodeState(
                                                  index - 1,
                                                  clampedFailedAttempts,
                                                  status,
                                                  ringJustCompleted
                                              )
                                            : null
                                    const nextState =
                                        index < maxAttempts - 1
                                            ? getAttemptNodeState(
                                                  index + 1,
                                                  clampedFailedAttempts,
                                                  status,
                                                  ringJustCompleted
                                              )
                                            : null
                                    const incomingLine =
                                        previousState == null
                                            ? 'spacer'
                                            : previousState === 'failed' &&
                                                nodeState === 'failed'
                                              ? 'error'
                                              : previousState === 'success'
                                                ? 'success'
                                                : previousState === 'failed' ||
                                                    previousState === 'active'
                                                  ? 'active'
                                                  : 'idle'
                                    const outgoingLine =
                                        nextState == null
                                            ? 'spacer'
                                            : nodeState === 'failed' &&
                                                nextState === 'failed'
                                              ? 'error'
                                              : nodeState === 'success'
                                                ? 'success'
                                                : nodeState === 'failed' ||
                                                    nodeState === 'active'
                                                  ? 'active'
                                                  : 'idle'
                                    const statusLabel =
                                        nodeState === 'failed'
                                            ? t(
                                                  'depositSuccessAnimation.attemptFailed'
                                              )
                                            : nodeState === 'success'
                                              ? t(
                                                    'depositSuccessAnimation.attemptSuccess'
                                                )
                                              : nodeState === 'active'
                                                ? t(
                                                      'depositSuccessAnimation.attemptInProgress'
                                                  )
                                                : t(
                                                      'depositSuccessAnimation.attemptWaiting'
                                                  )

                                    return (
                                        <li
                                            key={index}
                                            className={`deposit-success-animation__step deposit-success-animation__step--${nodeState}`}>
                                            <div className="deposit-success-animation__step-track">
                                                <span
                                                    className={`deposit-success-animation__step-line deposit-success-animation__step-line--${incomingLine}`}
                                                />
                                                <span className="deposit-success-animation__step-node">
                                                    {nodeState === 'failed' ? (
                                                        <StepFailIcon />
                                                    ) : nodeState ===
                                                      'success' ? (
                                                        <StepSuccessIcon />
                                                    ) : (
                                                        index + 1
                                                    )}
                                                </span>
                                                <span
                                                    className={`deposit-success-animation__step-line deposit-success-animation__step-line--${outgoingLine}`}
                                                />
                                            </div>
                                            <p className="deposit-success-animation__step-title">
                                                {t(
                                                    'depositSuccessAnimation.attemptNodeLabel',
                                                    { n: index + 1 }
                                                )}
                                            </p>
                                            <p className="deposit-success-animation__step-status">
                                                {statusLabel}
                                            </p>
                                        </li>
                                    )
                                }
                            )}
                        </ol>
                    </div>
                </div>
            ) : null}

            {isPromo ? (
                <div className="deposit-success-animation__promo">
                    <h1>{t('directBettingConfirmation.promoTitle')}</h1>
                    <p>{t('directBettingConfirmation.promoText')}</p>
                    <p className="deposit-success-animation__promo-hint">
                        {t('directBettingConfirmation.promoHint')}
                    </p>
                    {onPromoCodeChange ? (
                        <>
                            <div className="deposit-success-animation__promo-input-wrap">
                                <input
                                    value={promoCode}
                                    readOnly
                                    className="deposit-success-animation__promo-input"
                                    placeholder="----"
                                    aria-label={t(
                                        'directBettingConfirmation.promoCodeLabel'
                                    )}
                                />
                                <button
                                    type="button"
                                    className="deposit-success-animation__promo-clear"
                                    onClick={() => onPromoCodeChange('')}
                                    aria-label={t(
                                        'directBettingConfirmation.promoClear'
                                    )}>
                                    ×
                                </button>
                            </div>
                            <div className="deposit-success-animation__promo-pad">
                                {PROMO_CHARS.map((char) => (
                                    <button
                                        key={char}
                                        type="button"
                                        className="deposit-success-animation__promo-key"
                                        disabled={promoCode.length >= 4}
                                        onClick={() =>
                                            onPromoCodeChange(
                                                normalizePromoCode(
                                                    `${promoCode}${char}`
                                                )
                                            )
                                        }>
                                        {char}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="deposit-success-animation__promo-key deposit-success-animation__promo-key--wide"
                                    onClick={() =>
                                        onPromoCodeChange(promoCode.slice(0, -1))
                                    }
                                    aria-label={t(
                                        'directBettingConfirmation.promoDelete'
                                    )}>
                                    ⌫
                                </button>
                            </div>
                        </>
                    ) : null}
                </div>
            ) : (
                <div className="deposit-success-animation__hero">
                    <div
                        className={
                            isFailed
                                ? 'deposit-success-animation__ring deposit-success-animation__ring--failed'
                                : isSuccess
                                  ? ringJustCompleted
                                      ? isAwaitingBonus
                                          ? 'deposit-success-animation__ring deposit-success-animation__ring--succeeded deposit-success-animation__ring--complete deposit-success-animation__ring--bonus-pending'
                                          : 'deposit-success-animation__ring deposit-success-animation__ring--succeeded deposit-success-animation__ring--complete'
                                      : 'deposit-success-animation__ring deposit-success-animation__ring--succeeded'
                                  : isAwaitingResult
                                    ? 'deposit-success-animation__ring deposit-success-animation__ring--awaiting'
                                    : 'deposit-success-animation__ring'
                        }
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(
                            (ringProgress + goldProgress) * 50
                        )}
                        aria-label={
                            isFailed
                                ? t('directBettingConfirmation.errorTitle')
                                : isAwaitingBonus
                                  ? t(
                                        'depositSuccessAnimation.bonusPendingTitle'
                                    )
                                  : showGold
                                    ? t(
                                          'depositSuccessAnimation.creditedAmountLabel'
                                      )
                                    : isSuccess
                                      ? t(
                                            'depositSuccessAnimation.paidAmountLabel'
                                        )
                                      : t(
                                            'depositSuccessAnimation.inProgressLabel'
                                        )
                        }>
                        <DepositRadialRing
                            progress={ringProgress}
                            goldProgress={isFailed ? 0 : goldProgress}
                            failed={isFailed}
                            succeeded={isSuccess}
                            active={!isFailed && phase !== 'done'}
                        />
                        <div className="deposit-success-animation__ring-center">
                            <p className="deposit-success-animation__label">
                                {isFailed
                                    ? t('directBettingConfirmation.errorTitle')
                                    : showGold
                                      ? t(
                                            'depositSuccessAnimation.creditedAmountLabel'
                                        )
                                      : isSuccess
                                        ? t(
                                              'depositSuccessAnimation.paidAmountLabel'
                                          )
                                        : t(
                                              'depositSuccessAnimation.inProgressLabel'
                                          )}
                            </p>
                            <p
                                className={
                                    showGold
                                        ? 'deposit-success-animation__amount deposit-success-animation__amount--gold'
                                        : 'deposit-success-animation__amount'
                                }>
                                <span>{formattedAmount}</span>
                                <small>{currency}</small>
                            </p>
                            <div
                                className={
                                    isAwaitingBonus
                                        ? 'deposit-success-animation__bonus-slot deposit-success-animation__bonus-slot--pending'
                                        : showBadge
                                          ? 'deposit-success-animation__bonus-slot deposit-success-animation__bonus-slot--badge'
                                          : showGold
                                            ? 'deposit-success-animation__bonus-slot deposit-success-animation__bonus-slot--includes'
                                            : 'deposit-success-animation__bonus-slot'
                                }>
                                {isAwaitingBonus ? (
                                    <span className="deposit-success-animation__bonus-pending">
                                        <img
                                            src={giftBonus}
                                            alt=""
                                            width={26}
                                            height={26}
                                        />
                                        {anticipatedReward > 0
                                            ? t(
                                                  'depositSuccessAnimation.bonusPendingLabel',
                                                  {
                                                      amount: formattedAnticipatedReward,
                                                      currency:
                                                          anticipatedRewardCurrency
                                                  }
                                              )
                                            : t(
                                                  'depositSuccessAnimation.bonusPendingLabelGeneric'
                                              )}
                                    </span>
                                ) : showBadge ? (
                                    <span className="deposit-success-animation__badge">
                                        <img
                                            src={giftBonus}
                                            alt=""
                                            width={28}
                                            height={28}
                                        />
                                        {t(
                                            'depositSuccessAnimation.bonusBadge',
                                            {
                                                amount: formattedReward,
                                                currency
                                            }
                                        )}
                                    </span>
                                ) : null}
                                {showGold && !showBadge ? (
                                    <span className="deposit-success-animation__includes">
                                        <img
                                            src={giftBonus}
                                            alt=""
                                            width={26}
                                            height={26}
                                        />
                                        {t(
                                            'depositSuccessAnimation.includesBonus',
                                            {
                                                amount: formattedReward,
                                                currency
                                            }
                                        )}
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div
                className="deposit-success-animation__status"
                aria-live="polite">
                {isFailed ? (
                    <>
                        {isFinalFailure ? (
                            <>
                                <p className="deposit-success-animation__status-title">
                                    {t(
                                        'directBettingConfirmation.finalErrorText1'
                                    )}
                                </p>
                                <p className="deposit-success-animation__status-copy">
                                    {t(
                                        'directBettingConfirmation.finalErrorText2'
                                    )}
                                </p>
                                <p className="deposit-success-animation__status-copy">
                                    {t(
                                        'directBettingConfirmation.finalErrorText3'
                                    )}
                                </p>
                            </>
                        ) : (
                            <>
                                <p className="deposit-success-animation__status-title">
                                    {t('directBettingConfirmation.errorText1')}
                                </p>
                                <p className="deposit-success-animation__status-copy">
                                    {t('directBettingConfirmation.errorText2')}
                                </p>
                            </>
                        )}
                        <div className="deposit-success-animation__actions">
                            {canRetry && onRetry ? (
                                <button
                                    type="button"
                                    className="deposit-success-animation__action deposit-success-animation__action--primary"
                                    onClick={onRetry}
                                    disabled={isSubmitting}>
                                    {t('depositSuccessAnimation.retryButton')}
                                </button>
                            ) : null}
                            {!isFinalFailure && onChangeAccountId ? (
                                <button
                                    type="button"
                                    className="deposit-success-animation__action deposit-success-animation__action--secondary"
                                    onClick={onChangeAccountId}
                                    disabled={isSubmitting}>
                                    {t(
                                        'depositSuccessAnimation.changeIdButton'
                                    )}
                                </button>
                            ) : null}
                            {showVoucherOption ? (
                                <button
                                    type="button"
                                    className="deposit-success-animation__action deposit-success-animation__action--ghost"
                                    onClick={onShowVoucher}
                                    disabled={isSubmitting}>
                                    {t(
                                        'directBettingConfirmation.showVoucherButton'
                                    )}
                                </button>
                            ) : null}
                        </div>
                    </>
                ) : isPromo ? (
                    <div className="deposit-success-animation__actions">
                        {onRetry ? (
                            <button
                                type="button"
                                className="deposit-success-animation__action deposit-success-animation__action--primary"
                                onClick={onRetry}
                                disabled={isSubmitting}>
                                {t('directBettingConfirmation.confirmButton')}
                            </button>
                        ) : null}
                        {onChangeAccountId ? (
                            <button
                                type="button"
                                className="deposit-success-animation__action deposit-success-animation__action--secondary"
                                onClick={onChangeAccountId}
                                disabled={isSubmitting}>
                                {t('depositSuccessAnimation.changeIdButton')}
                            </button>
                        ) : null}
                    </div>
                ) : isAwaitingBonus ? (
                    <>
                        <p className="deposit-success-animation__status-title">
                            {t('depositSuccessAnimation.bonusPendingTitle')}
                        </p>
                        <p className="deposit-success-animation__status-copy">
                            {t('depositSuccessAnimation.bonusPendingSubtitle')}
                        </p>
                        <div
                            className="deposit-success-animation__dots deposit-success-animation__dots--bonus"
                            aria-hidden="true">
                            <span />
                            <span />
                            <span />
                        </div>
                    </>
                ) : (
                    <>
                        <p className="deposit-success-animation__status-title">
                            {t('depositSuccessAnimation.inProgressTitle')}
                        </p>
                        <p className="deposit-success-animation__status-copy">
                            {t('depositSuccessAnimation.inProgressSubtitle')}
                        </p>
                        <div
                            className="deposit-success-animation__dots"
                            aria-hidden="true">
                            <span />
                            <span />
                            <span />
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}
